import { useMutation, useQueryClient } from "react-query";
import { loggedInApiCall } from "src/api/axiosClient/userApi";
import { ApiError } from "./useGetAllApplications";
import { ExtendedWebhook } from "./useGetOneApplication";

const jsonHeaders = {
    "Content-Type": "application/json",
    Accept: "application/json",
};

export interface WebhookData {
    active?: boolean;
    name?: string;
    method?: string;
    headers?: Record<string, string>;
    retryCount?: number;
    endpoint?: string;
    eventName?: string;
}

type CreateWebhookOptions = {
    applicationId: string;
    webhook: WebhookData;
};

export const useCreateWebhook = () => {
    const queryClient = useQueryClient();

    const mutation = useMutation<ExtendedWebhook, ApiError, CreateWebhookOptions>(
        async (options: CreateWebhookOptions) => {
            const { applicationId, webhook } = options;

            // Ensure we have the required fields
            if (!webhook.endpoint) {
                throw new Error("Either endpoint or url is required");
            }

            if (!webhook.eventName) {
                throw new Error("eventName is required");
            }

            if (!webhook.name) {
                throw new Error("Webhook name is required");
            }

            // Create the API webhook object with proper types
            const apiWebhook = {
                endpoint: String(webhook.endpoint || ""),
                eventName: webhook.eventName ? String(webhook.eventName) : "",
                active: webhook.active !== undefined ? Boolean(webhook.active) : true,
                name: String(webhook.name || ""),
                method: webhook.method ? String(webhook.method) : "POST",
                headers: webhook.headers || {},
                retryCount: webhook.retryCount !== undefined ? Number(webhook.retryCount) : 3,
                application: `/api/integrations/applications/${applicationId}`,
            };

            const response = await loggedInApiCall.post(
                `/api/integrations/applications/${applicationId}/webhooks`,
                apiWebhook,
                {
                    headers: jsonHeaders,
                },
            );

            return response.data;
        },
        {
            onSuccess: (data, { applicationId }) => {
                queryClient.invalidateQueries(["applicationWebhooks", applicationId]);
            },
            onError: (error: ApiError) => {
                if (error.response?.status === 422) {
                    const validationErrors = error.response.data.violations;
                    if (validationErrors && Array.isArray(validationErrors)) {
                        return Promise.reject(
                            validationErrors.reduce(
                                (acc, violation) => ({
                                    ...acc,
                                    [violation.propertyPath]: violation.message,
                                }),
                                {},
                            ),
                        );
                    }
                }
                return Promise.reject(error.message ? { name: error.message } : new Error("Failed to create webhook"));
            },
        },
    );

    return mutation;
};
